import {
  AccountInfo,
  AuthenticationResult,
  Configuration,
  EndSessionRequest,
  InteractionRequiredAuthError,
  LogLevel,
  PublicClientApplication,
  RedirectRequest,
  SilentRequest,
} from '@azure/msal-browser';
import * as Bixby from '$/core';
import { store } from '$/store';
import { resumeFromMsalCache } from '$/store/msal/actions';

/**
 * Configuration class for @azure/msal-browser:
 * https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-browser/modules/_src_config_configuration_.html
 */
const MSAL_CONFIG: Configuration = {
  auth: {
    clientId: Bixby.Configuration.msalClientId,
    authority: Bixby.Configuration.msalAuthority,
    redirectUri: Bixby.Configuration.msalRedirectUri,
  },
  cache: {
    cacheLocation: 'localStorage', // This configures where your cache will be stored
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            // eslint-disable-next-line no-console
            console.error(message);
            return;
          case LogLevel.Info:
            // eslint-disable-next-line no-console
            console.info(message);
            return;
          case LogLevel.Verbose:
            // eslint-disable-next-line no-console
            console.debug(message);
            return;
          case LogLevel.Warning:
            // eslint-disable-next-line no-console
            console.warn(message);
            return;
        }
      },
    },
  },
};

export abstract class MsalService {
  private static myMSALObj: PublicClientApplication = new PublicClientApplication(MSAL_CONFIG);
  private static account: AccountInfo;
  private static loginScopes: Array<string> = [];
  private static plannerScopes: Array<string> = ['Group.ReadWrite.All'];
  private static silentPlannerScopes: Array<string> = ['openid', 'profile', 'Group.ReadWrite.All'];
  private static peopleScopes: Array<string> = ['Directory.Read.All'];
  private static silentPeopleScopes: Array<string> = ['openid', 'profile', 'Directory.Read.All'];

  public static getAccount(): AccountInfo {
    return MsalService.account;
  }

  public static resumeFromCache(): boolean {
    const account = MsalService.determineAccount();
    if (account !== null) {
      MsalService.account = account;
      return true;
    }
    return false;
  }

  /**
   * Calls loginRedirect.
   * @param signInType
   */
  public static async loginAsync(): Promise<void> {
    await MsalService.myMSALObj.loginRedirect({
      scopes: MsalService.loginScopes,
      redirectStartPage: window.location.href,
    });
  }

  /**
   * Checks whether we are in the middle of a redirect and handles state accordingly. Only required for redirect flows.
   *
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/initialization.md#redirect-apis
   */
  public static async loadAuthModule(): Promise<void> {
    const response: AuthenticationResult | null = await MsalService.myMSALObj.handleRedirectPromise();
    MsalService.handleResponse(response);
    if (MsalService.account !== null) {
      store.dispatch(resumeFromMsalCache() as any);
    }
  }

  /**
   * Logs out of current account.
   */
  public static logout(): void {
    const logOutRequest: EndSessionRequest = {
      account: MsalService.account,
    };

    MsalService.myMSALObj.logout(logOutRequest);
  }

  /**
   * Gets the token to submit tasks to planner silently, or falls back to interactive redirect.
   */
  public static async getPlannerTokenRedirect(): Promise<string | null> {
    // https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-browser/modules/_src_request_silentrequest_.html
    const silentPlannerRequest: SilentRequest = {
      scopes: MsalService.silentPlannerScopes,
      account: MsalService.account,
      forceRefresh: false,
    };
    return MsalService.getTokenRedirect(silentPlannerRequest, {
      scopes: MsalService.plannerScopes,
      redirectStartPage: window.location.href,
    });
  }

  /**
   * Gets the token to search people silently, or falls back to interactive redirect.
   */
  public static async getPeopleSearchTokenRedirect(): Promise<string | null> {
    // https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-browser/modules/_src_request_silentrequest_.html
    const silentPeopleRequest: SilentRequest = {
      scopes: MsalService.silentPeopleScopes,
      account: MsalService.account,
      forceRefresh: false,
    };
    return MsalService.getTokenRedirect(silentPeopleRequest, {
      scopes: MsalService.peopleScopes,
      redirectStartPage: window.location.href,
    });
  }

  /**
   * Gets a token silently, or falls back to interactive redirect.
   */
  private static async getTokenRedirect(
    silentRequest: SilentRequest,
    interactiveRequest: RedirectRequest
  ): Promise<string | null> {
    let result: string | null = null;
    try {
      const response = await MsalService.myMSALObj.acquireTokenSilent(silentRequest);
      result = response.accessToken;
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.log('silent token acquisition fails.');
      if (e instanceof InteractionRequiredAuthError) {
        // eslint-disable-next-line no-console
        console.log('acquiring token using redirect');
        // eslint-disable-next-line no-console
        MsalService.myMSALObj.acquireTokenRedirect(interactiveRequest).catch(console.error);
      } else {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }

    return result;
  }

  /**
   * Calls getAllAccounts and determines the correct account to sign into, currently defaults to first account found in cache.
   * TODO: Add account chooser code
   *
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
   */
  private static determineAccount(): AccountInfo | null {
    // need to call getAccount here?

    let result: AccountInfo | null = null;
    const currentAccounts = MsalService.myMSALObj.getAllAccounts();
    if (currentAccounts === null) {
      // eslint-disable-next-line no-console
      console.log('No accounts detected');
    } else if (currentAccounts.length > 1) {
      // Add choose account code here
      // eslint-disable-next-line no-console
      console.log('Multiple accounts detected, need to add choose account code.');
      result = currentAccounts[0];
    } else if (currentAccounts.length === 1) {
      result = currentAccounts[0];
    }

    return result;
  }

  /**
   * Handles the response from a popup or redirect. If response is null, will check if we have any accounts and attempt to sign in.
   * @param response
   */
  private static handleResponse(response: AuthenticationResult | null) {
    if (response !== null && response.account !== null) {
      MsalService.account = response.account;
    } else {
      const account = MsalService.determineAccount();
      if (account !== null) {
        MsalService.account = account;
      }
    }
  }
}
